import React, { useMemo, useRef } from 'react';
import styles from './SettingsDropdown.module.scss';
import { useTranslation } from 'react-i18next';
import SelectOption from 'engage/components/SelectOption/SelectOption';
import { ORIENTATIONS, LANGUAGES, PARTICIPANTS } from 'engage/constants/projectCreatorConsts';

const SettingsDropdown = ({
    isOpen,
    onClose,
    videoOrientation,
    onOrientationChange,
    selectedLanguage,
    onLanguageChange,
    numberOfClips,
    onNumberOfClipsChange,
    selectedParticipants,
    onParticipantsChange,
}) => {
    const { t } = useTranslation();
    const dropdownRef = useRef(null);

    const orientationOptions = useMemo(() => {
        return Object.values(ORIENTATIONS).map(orientation => ({
            ...orientation,
            name: orientation.text,
            id: orientation.type
        }));
    }, []);

    const currentOrientation = useMemo(() => {
        const orientation = Object.values(ORIENTATIONS).find(o => o.type === videoOrientation.type);
        return orientation ? {
            ...orientation,
            name: orientation.text,
            id: orientation.type
        } : null;
    }, [videoOrientation]);

    const durationOptions = useMemo(() => {
        return [
            { name: t('home_page.storyboard_widget.duration.any_length'), type: null },
            { name: t('home_page.storyboard_widget.duration.very_short'), type: 1 },
            { name: t('home_page.storyboard_widget.duration.short'), type: 3 },
            { name: t('home_page.storyboard_widget.duration.medium'), type: 5 },
            { name: t('home_page.storyboard_widget.duration.long'), type: 7 },
            { name: t('home_page.storyboard_widget.duration.very_long'), type: 10 }
        ];
    }, [t]);

    const currentDurationOption = useMemo(() => {
        return durationOptions.find(option => option.type === numberOfClips) || durationOptions[0];
    }, [numberOfClips, durationOptions]);

    const participantsOptions = useMemo(() => {
        return Object.values(PARTICIPANTS);
    }, []);

    const currentParticipantsOption = useMemo(() => {
        return Object.values(PARTICIPANTS).find(option => option.type === selectedParticipants?.type) || Object.values(PARTICIPANTS)[0];
    }, [selectedParticipants]);

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            const isOutsideSettingsDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);

            const isOnSelectOptionDropdown = event.target.closest('[class*="customSelectContainer"]');

            const isOnSettingsButton = event.target.closest('[class*="settingsButton"]');

            if (isOutsideSettingsDropdown && !isOnSelectOptionDropdown && !isOnSettingsButton) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    const handleOrientationChange = (orientation) => {
        onOrientationChange(orientation.type);
    };

    const handleLanguageChange = (language) => {
        onLanguageChange(language);
    };

    const handleClipsChange = (clipsOption) => {
        onNumberOfClipsChange(clipsOption.type);
    };

    const handleParticipantsChange = (participantsOption) => {
        onParticipantsChange(participantsOption);
    };

    if (!isOpen) return null;

    return (
        <div ref={dropdownRef} className={styles.settingsDropdown}>
            <div className={styles.dropdownContent}>
                <div className={styles.settingSection}>
                    <SelectOption
                        title={t('home_page.storyboard_widget.orientation')}
                        currentlySelected={currentOrientation}
                        options={orientationOptions}
                        onSelect={handleOrientationChange}
                        dropdownClassName={styles.customSelect}
                    />
                </div>

                <div className={styles.settingSection}>
                    <SelectOption
                        title={t('home_page.storyboard_widget.language')}
                        currentlySelected={selectedLanguage}
                        options={Object.values(LANGUAGES)}
                        onSelect={handleLanguageChange}
                        dropdownClassName={styles.customSelect}
                    />
                </div>

                <div className={styles.settingSection}>
                    <SelectOption
                        title={t('home_page.storyboard_widget.duration')}
                        currentlySelected={currentDurationOption}
                        options={durationOptions}
                        onSelect={handleClipsChange}
                        dropdownClassName={styles.customSelect}
                    />
                </div>

                <div className={styles.settingSection}>
                    <SelectOption
                        title={t('home_page.storyboard_widget.participants')}
                        currentlySelected={currentParticipantsOption}
                        options={participantsOptions}
                        onSelect={handleParticipantsChange}
                        dropdownClassName={styles.customSelect}
                    />
                </div>
            </div>
        </div>
    );
};

export default SettingsDropdown; 